import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Autorenew } from '@mui/icons-material';
import { ssoAPI } from '../services/api';

export function formatSecretExpiry(dateString?: string | null): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export interface RenewClientSecretResult {
  client_secret?: string;
  client_secret_expires_at?: string;
}

interface RenewClientSecretControlProps {
  configId: string;
  applicationName?: string;
  connectionName?: string;
  currentExpiresAt?: string | null;
  disabled?: boolean;
  variant?: 'icon' | 'button';
  onRenewed: (result: RenewClientSecretResult) => void;
  onError: (message: string) => void;
}

const RenewClientSecretControl: React.FC<RenewClientSecretControlProps> = ({
  configId,
  applicationName,
  connectionName,
  currentExpiresAt,
  disabled = false,
  variant = 'icon',
  onRenewed,
  onError,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const targetLabel = [applicationName, connectionName].filter(Boolean).join(' / ') || 'this OIDC configuration';

  const handleConfirmRenew = async () => {
    setRenewing(true);
    try {
      const response = await ssoAPI.renewClientSecret(configId);
      const payload = response.data?.data;
      if (!response.data?.success || !payload) {
        throw new Error(response.data?.error || 'Failed to renew client secret');
      }
      onRenewed({
        client_secret: payload.client_secret,
        client_secret_expires_at: payload.client_secret_expires_at,
      });
      setConfirmOpen(false);
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        'Failed to renew client secret';
      onError(message);
    } finally {
      setRenewing(false);
    }
  };

  const triggerDisabled = disabled || renewing;

  return (
    <>
      {variant === 'button' ? (
        <Button
          size="small"
          variant="outlined"
          color="warning"
          startIcon={renewing ? <CircularProgress size={14} color="inherit" /> : <Autorenew />}
          disabled={triggerDisabled}
          onClick={() => setConfirmOpen(true)}
        >
          Renew Secret
        </Button>
      ) : (
        <Tooltip title="Renew client secret">
          <span>
            <IconButton
              size="small"
              color="warning"
              disabled={triggerDisabled}
              onClick={() => setConfirmOpen(true)}
              sx={{ p: 0.25 }}
            >
              {renewing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Autorenew sx={{ fontSize: '1rem' }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Dialog open={confirmOpen} onClose={() => !renewing && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Renew client secret?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This will generate a new client secret for <strong>{targetLabel}</strong> in the identity provider.
            Update your application with the new secret after renewal.
          </Typography>
          {currentExpiresAt && (
            <Typography variant="body2" color="text.secondary">
              Current expiry: <strong>{formatSecretExpiry(currentExpiresAt)}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={renewing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void handleConfirmRenew()}
            disabled={renewing}
            startIcon={renewing ? <CircularProgress size={16} color="inherit" /> : <Autorenew />}
          >
            {renewing ? 'Renewing…' : 'Renew Secret'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RenewClientSecretControl;
